import { useEffect, useRef, useState } from 'react';

export interface NotchedWhiteNoiseState {
  isPlaying: boolean;
  volume: number;
  notchFrequency: number;
  notchBandwidth: number;
  playNotchedNoise: () => void;
  stopNotchedNoise: () => void;
  setVolume: (volume: number) => void;
  setNotchFrequency: (frequency: number) => void;
  setNotchBandwidth: (bandwidth: number) => void;
}

// Research-based bandwidth calculations
export const calculateHalfOctaveBandwidth = (frequency: number): number => {
  // 0.5 octave bandwidth: f_upper - f_lower where f_upper = f * 2^0.25, f_lower = f * 2^-0.25
  const halfOctaveUp = frequency * Math.pow(2, 0.25);
  const halfOctaveDown = frequency * Math.pow(2, -0.25);
  return Math.round(halfOctaveUp - halfOctaveDown);
};

export const calculateERBBandwidth = (frequency: number): number => {
  // ERB (Equivalent Rectangular Bandwidth) formula: ERB = 107.94 × f + 24.7 (f in kHz)
  const erb = 107.94 * (frequency / 1000) + 24.7;
  return Math.round(2 * erb); // 2 ERB as per research recommendations
};


export const useNotchedWhiteNoise = (
  initialFrequency: number = 1000,
  initialVolume: number = 0.3
): NotchedWhiteNoiseState => {
  const audioContextRef = useRef<AudioContext | null>(null);
  const noiseSourceRef = useRef<AudioBufferSourceNode | null>(null);
  const gainNodeRef = useRef<GainNode | null>(null);
  const notchFilterRef = useRef<BiquadFilterNode | null>(null);
  
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolumeState] = useState(initialVolume);
  const [notchFrequency, setNotchFrequencyState] = useState(initialFrequency);
  const [notchBandwidth, setNotchBandwidthState] = useState(
    calculateHalfOctaveBandwidth(initialFrequency) // Start with research-based default
  );

  // Update notch frequency and auto-adjust bandwidth when initialFrequency prop changes
  useEffect(() => {
    setNotchFrequencyState(initialFrequency);
    setNotchBandwidthState(calculateHalfOctaveBandwidth(initialFrequency));
  }, [initialFrequency]);

  useEffect(() => {
    // Initialize AudioContext on first user interaction
    const initAudioContext = () => {
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
      }
    };

    document.addEventListener('click', initAudioContext, { once: true });
    document.addEventListener('touchstart', initAudioContext, { once: true });

    return () => {
      document.removeEventListener('click', initAudioContext);
      document.removeEventListener('touchstart', initAudioContext);
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, []);

  const generateWhiteNoiseBuffer = (duration: number = 2): AudioBuffer => {
    if (!audioContextRef.current) {
      throw new Error('AudioContext not initialized');
    }

    const audioContext = audioContextRef.current;
    const sampleRate = audioContext.sampleRate;
    const bufferLength = sampleRate * duration;
    const buffer = audioContext.createBuffer(2, bufferLength, sampleRate);

    // Generate white noise for both channels
    for (let channel = 0; channel < 2; channel++) {
      const channelData = buffer.getChannelData(channel);
      for (let i = 0; i < bufferLength; i++) {
        channelData[i] = Math.random() * 2 - 1; // Random values between -1 and 1
      }
    }

    return buffer;
  };

  const createNotchFilter = (frequency: number, bandwidth: number): BiquadFilterNode => {
    if (!audioContextRef.current) {
      throw new Error('AudioContext not initialized');
    }

    const filter = audioContextRef.current.createBiquadFilter();
    filter.type = 'notch';
    filter.frequency.value = frequency;
    filter.Q.value = frequency / bandwidth; // Q factor determines bandwidth
    return filter;
  };

  const playNotchedNoise = () => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
    }

    // Stop any existing noise
    stopNotchedNoise();

    const audioContext = audioContextRef.current;
    
    // Create white noise buffer
    const noiseBuffer = generateWhiteNoiseBuffer(2);
    
    // Create audio nodes
    const noiseSource = audioContext.createBufferSource();
    const gainNode = audioContext.createGain();
    const notchFilter = createNotchFilter(notchFrequency, notchBandwidth);

    // Configure noise source
    noiseSource.buffer = noiseBuffer;
    noiseSource.loop = true;

    // Connect audio graph: source -> notch filter -> gain -> destination
    noiseSource.connect(notchFilter);
    notchFilter.connect(gainNode);
    gainNode.connect(audioContext.destination);

    // Set initial volume with smooth ramp
    gainNode.gain.setValueAtTime(0, audioContext.currentTime);
    gainNode.gain.linearRampToValueAtTime(volume, audioContext.currentTime + 0.1);

    // Store references
    noiseSourceRef.current = noiseSource;
    gainNodeRef.current = gainNode;
    notchFilterRef.current = notchFilter;

    // Start playing
    noiseSource.start();
    setIsPlaying(true);
  };

  const stopNotchedNoise = () => {
    if (noiseSourceRef.current && gainNodeRef.current && audioContextRef.current) {
      const audioContext = audioContextRef.current;
      
      // Smooth volume ramp down to avoid clicks
      gainNodeRef.current.gain.linearRampToValueAtTime(0, audioContext.currentTime + 0.1);
      
      setTimeout(() => {
        if (noiseSourceRef.current) {
          noiseSourceRef.current.stop();
          noiseSourceRef.current.disconnect();
          noiseSourceRef.current = null;
        }
        if (gainNodeRef.current) {
          gainNodeRef.current.disconnect();
          gainNodeRef.current = null;
        }
        if (notchFilterRef.current) {
          notchFilterRef.current.disconnect();
          notchFilterRef.current = null;
        }
      }, 100);
    }
    setIsPlaying(false);
  };

  const setVolume = (newVolume: number) => {
    const clampedVolume = Math.max(0, Math.min(1, newVolume));
    setVolumeState(clampedVolume);
    
    if (gainNodeRef.current && audioContextRef.current) {
      gainNodeRef.current.gain.linearRampToValueAtTime(
        clampedVolume,
        audioContextRef.current.currentTime + 0.05
      );
    }
  };

  const setNotchFrequency = (frequency: number) => {
    const clampedFrequency = Math.max(20, Math.min(20000, frequency));
    setNotchFrequencyState(clampedFrequency);
    
    if (notchFilterRef.current && audioContextRef.current) {
      notchFilterRef.current.frequency.linearRampToValueAtTime(
        clampedFrequency,
        audioContextRef.current.currentTime + 0.05
      );
      // Update Q factor to maintain bandwidth
      notchFilterRef.current.Q.value = clampedFrequency / notchBandwidth;
    }
  };

  const setNotchBandwidth = (bandwidth: number) => {
    // Updated limits to accommodate research-based bandwidths (up to ~4000 Hz for 1 octave at 8kHz)
    const clampedBandwidth = Math.max(50, Math.min(5000, bandwidth));
    setNotchBandwidthState(clampedBandwidth);
    
    if (notchFilterRef.current) {
      notchFilterRef.current.Q.value = notchFrequency / clampedBandwidth;
    }
  };

  return {
    isPlaying,
    volume,
    notchFrequency,
    notchBandwidth,
    playNotchedNoise,
    stopNotchedNoise,
    setVolume,
    setNotchFrequency,
    setNotchBandwidth,
  };
};